/** Workman global date formatting — DD-MM-YYYY (per UI mandate 2026-05-09).
 *
 * Use these helpers everywhere. Do not call `toLocaleDateString` / build
 * date strings inline — `DD-MM-YYYY` is the canonical display format
 * across every page, drawer, dialog, chip subtitle, and detail surface.
 */

const pad = (n: number): string => String(n).padStart(2, "0");

/**
 * Format a Date or ISO string as `DD-MM-YYYY`. Accepts:
 * - Date instance
 * - ISO datetime ("2026-05-09T12:30:00Z")
 * - ISO date-only ("2026-05-09") — parsed as local date (no timezone shift).
 *
 * Returns `"—"` for null / empty / invalid input.
 */
export function formatDate(value: string | Date | null | undefined): string {
  if (!value) return "—";
  let d: Date;
  if (value instanceof Date) {
    d = value;
  } else if (/^\d{4}-\d{2}-\d{2}$/.test(value)) {
    // Date-only ISO — parse as local to avoid TZ rolling backward.
    const parts = value.split("-").map(Number);
    d = new Date(parts[0]!, parts[1]! - 1, parts[2]!);
  } else {
    d = new Date(value);
  }
  if (Number.isNaN(d.getTime())) return "—";
  return `${pad(d.getDate())}-${pad(d.getMonth() + 1)}-${d.getFullYear()}`;
}

/** Format ISO datetime as `DD-MM-YYYY HH:MM AM/PM`. Returns "—" for null/empty. */
export function formatDateTime(iso: string | Date | null | undefined): string {
  if (!iso) return "—";
  const d = iso instanceof Date ? iso : new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  const date = `${pad(d.getDate())}-${pad(d.getMonth() + 1)}-${d.getFullYear()}`;
  let h = d.getHours();
  const m = pad(d.getMinutes());
  const ampm = h >= 12 ? "PM" : "AM";
  h = h % 12 || 12;
  return `${date} ${pad(h)}:${m} ${ampm}`;
}

/** Time-only — `HH:MM AM/PM`. */
export function formatTime(iso: string | Date | null | undefined): string {
  if (!iso) return "—";
  const d = iso instanceof Date ? iso : new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  let h = d.getHours();
  const m = pad(d.getMinutes());
  const ampm = h >= 12 ? "PM" : "AM";
  h = h % 12 || 12;
  return `${pad(h)}:${m} ${ampm}`;
}

/** File size — `512 B`, `4.2 KB`, `1.3 MB`, `2.05 GB`. */
export function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  if (n < 1024 * 1024 * 1024) return `${(n / 1024 / 1024).toFixed(1)} MB`;
  return `${(n / 1024 / 1024 / 1024).toFixed(2)} GB`;
}
